import { Badge } from "../../../../components/ui/badge";
import { WorkpaperFocusControls } from "./WorkpaperFocusControls";

interface WorkpaperFocusBarProps {
  sessionName: string;
  status: string;
  /** Expand the full page header (this bar is replaced by it). */
  onToggleHeader: () => void;
  /** Leave focus mode — back to the full workpaper table. */
  onExit: () => void;
}

const statusBadge: Record<
  string,
  { label: string; variant: "secondary" | "outline" | "default" }
> = {
  in_progress: { label: "In Progress", variant: "secondary" },
  in_review: { label: "In Review", variant: "outline" },
  complete: { label: "Complete", variant: "default" },
};

/**
 * Condensed ~44px identity bar shown in the work-paper "focus mode" (a cell
 * fold-out / doc viewer is open and the three-pane workspace wants full
 * viewport height). The left corner carries the focus controls — the chevron
 * that swaps this bar for the full page header, and the "Back to Workpaper"
 * exit — followed by session name + status for orientation. Nothing here
 * reacts to hover, so mousing across the bar never moves the layout.
 */
export const WorkpaperFocusBar = ({
  sessionName,
  status,
  onToggleHeader,
  onExit,
}: WorkpaperFocusBarProps) => {
  const badge = statusBadge[status] ?? statusBadge.in_progress;

  return (
    <div className="flex items-center gap-2 h-11 px-4 border-b border-stone-200">
      <WorkpaperFocusControls
        headerExpanded={false}
        onToggleHeader={onToggleHeader}
        onExit={onExit}
      />
      <span aria-hidden="true" className="h-5 w-px bg-stone-200" />
      <span className="font-medium text-sm truncate max-w-[280px]">
        {sessionName}
      </span>
      <span className="text-muted-foreground text-sm hidden sm:inline">
        · Workpaper
      </span>
      <Badge variant={badge.variant} className="ml-1 flex-shrink-0">
        {badge.label}
      </Badge>
    </div>
  );
};
